import { ItemTitle } from '@sleepstation/components'
import {
  BreakingLayout,
  HList,
  VLayout,
} from '@sleepstation/components-layouts'
import propTypes from '@sleepstation/components-prop-types'
import { DIRECTION, LAYOUT_ALIGN, LAYOUT_CHILD } from '@sleepstation/const'
import { marginBottom, mq, paddingH } from '@sleepstation/style-api-helpers'
import { defaultTheme } from '@sleepstation/themes-public-site'
import anime from 'animejs'
import PropTypes from 'prop-types'
import { mapIndexed } from 'ramda-adjunct'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSwipeable } from 'react-swipeable'
import { useWindowSize } from 'react-use'
import styled, { ThemeProvider, css } from 'styled-components'
import Nav from './Nav'
import ProgressIndicator from './Nav/ProgressIndicator'

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

const MAP = [`xSmall`, `small`, `medium`, `large`]

const SPACING = 1

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------

const getInnerWidth = (breakpointIdx) => (props) => {
  const { count, theme } = props

  const percentWidthOfItem = 100
  const percentWidthOfInner = percentWidthOfItem * count

  const spacingValue = theme.api.getHr(SPACING, MAP[breakpointIdx])

  const hiddenItems = count - 1

  const spacingAdjustment = spacingValue * hiddenItems

  return css`
    width: calc(${percentWidthOfInner}% + ${spacingAdjustment}rem);
  `
}

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------

const renderItems = (renderer, items) =>
  mapIndexed((item, idx) => <li key={idx}>{renderer(item, idx)}</li>, items)

const animateScroll = (innerRef, scrollerRef, animeRef, index) => {
  if (animeRef.current) {
    animeRef.current.pause()
  }

  const innerElement = innerRef.current
  const child = innerElement.childNodes[0]

  const childWidth = child.offsetWidth
  // No need to worry about referencing window. This is never executed on
  // the server
  const spacing = window.getComputedStyle(innerElement).paddingLeft

  const offset = childWidth + parseInt(spacing, 10)
  const scrollerElement = scrollerRef.current
  const scrollLeft = offset * index

  animeRef.current = anime({
    targets: scrollerElement,
    scrollLeft,
    duration: 500,
    easing: `easeInOutQuad`,
    complete: () => {
      animeRef.current = null
    },
  })
}

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

const Scroller = styled.div`
  position: relative;
  max-width: 100%;
  overflow-x: hidden;
`

const Inner = styled(HList).attrs({ child: LAYOUT_CHILD.EQUAL })`
  position: relative;
  z-index: 1;
  ${paddingH(SPACING)}

  ${mq(`smallOnly`)} {
    ${getInnerWidth(1)}
  }

  ${mq(`smallUp`)} {
    ${marginBottom(1)}
  }

  ${mq(`mediumOnly`)} {
    ${getInnerWidth(2)}
  }

  ${mq(`largeUp`)} {
    ${getInnerWidth(3)}
  }

  ${mq(`xSmallOnly`)} {
    ${getInnerWidth(0)}
  }
`

const Footer = styled(BreakingLayout).attrs({
  as: `footer`,
  direction: DIRECTION.VERTICAL,
  align: [LAYOUT_ALIGN.CENTER, LAYOUT_ALIGN.RIGHT],
})``

const Layout = styled(VLayout).attrs({
  spacing: [1.75, 0],
  align: LAYOUT_ALIGN.CENTER,
})`
  > ${ItemTitle} {
    ${marginBottom(2)}
  }
`

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const Deck = ({ deck, renderer, spacing }) => {
  const scrollerRef = useRef(null)
  const innerRef = useRef(null)
  const animeRef = useRef(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const size = useWindowSize()

  const { cards } = deck

  const isLastCard = currentIndex === cards.length - 1
  const isFirstCard = currentIndex === 0

  useEffect(() => {
    animateScroll(innerRef, scrollerRef, animeRef, currentIndex)
  }, [size, innerRef, scrollerRef, animeRef, currentIndex])

  const navigateNext = useCallback(() => {
    if (currentIndex < cards.length - 1) {
      setCurrentIndex((v) => v + 1)
      animateScroll(innerRef, scrollerRef, animeRef, currentIndex)
    }
  }, [innerRef, scrollerRef, animeRef, currentIndex, cards.length])

  const navigatePrevious = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex((v) => v - 1)
      animateScroll(innerRef, scrollerRef, animeRef, currentIndex)
    }
  }, [innerRef, scrollerRef, animeRef, currentIndex])

  const handlers = useSwipeable({
    onSwipedLeft: navigateNext,
    onSwipedRight: navigatePrevious,
  })

  return (
    <Layout {...handlers}>
      {deck.title && <ItemTitle>{deck.title}</ItemTitle>}
      <Scroller ref={scrollerRef}>
        <ThemeProvider theme={defaultTheme}>
          <Inner ref={innerRef} count={cards.length} spacing={spacing}>
            {renderItems(renderer, cards)}
          </Inner>
        </ThemeProvider>
      </Scroller>
      <Footer>
        <Nav
          onNext={navigateNext}
          onPrevious={navigatePrevious}
          {...(deck.skipLink && { skipLink: deck.skipLink })}
          completeLink={deck.completeLink}
          nextButton={deck.nextButton}
          hasNext={!isLastCard}
          hasPrevious={!isFirstCard}
        >
          <ProgressIndicator count={cards.length} currentIndex={currentIndex} />
        </Nav>
      </Footer>
    </Layout>
  )
}

Deck.propTypes = {
  deck: PropTypes.object.isRequired,
  renderer: PropTypes.func.isRequired,
  spacing: propTypes.stringOrNumberOrArrayOfStringsOrNumbers,
}

Deck.defaultProps = {
  spacing: 1,
}

export default Deck
